import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentService } from '../../services/appointmentService';
import { Appointment, AppointmentStatus } from '../../types/appointment.types';
import AppointmentManageModal from '../../components/client/AppointmentManageModal';
import './AppointmentHistory.css';
import { getLocalDateString } from '../../utils/dateUtils';

type FilterType = 'all' | 'upcoming' | 'completed' | 'cancelled';

const AppointmentHistory: React.FC = () => {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterType>('all');
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [showManageModal, setShowManageModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadAppointments();
    }, []);

    useEffect(() => {
        filterAppointments();
    }, [appointments, filter]);

    const loadAppointments = async () => {
        try {
            const data = await appointmentService.getMyAppointments();
            const sorted = data.sort((a, b) =>
                new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime()
            );
            setAppointments(sorted);
        } catch (error) {
            console.error('Error loading appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterAppointments = () => {
        const today = getLocalDateString();

        let filtered: Appointment[];

        switch (filter) {
            case 'upcoming':
                filtered = appointments.filter(a =>
                    a.appointmentDate >= today &&
                    (a.status === 'CONFIRMED' || a.status === 'PENDING')
                );
                break;
            case 'completed':
                filtered = appointments.filter(a => a.status === 'COMPLETED');
                break;
            case 'cancelled':
                filtered = appointments.filter(a =>
                    a.status === 'CANCELLED' || a.status === 'NO_SHOW'
                );
                break;
            default:
                filtered = appointments;
        }

        setFilteredAppointments(filtered);
    };

    const handleManageAppointment = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setShowManageModal(true);
    };

    const canManage = (appointment: Appointment): boolean => {
        return (
            appointment.status === 'CONFIRMED' || appointment.status === 'PENDING'
        );
    };

    const getStatusConfig = (appointment: Appointment) => {
        const now = new Date();
        const appointmentDateTime = new Date(`${appointment.appointmentDate}T${appointment.startTime}`);

        // Virtual status for expired appointments
        if (appointmentDateTime < now && (appointment.status === 'CONFIRMED' || appointment.status === 'PENDING')) {
            return { class: 'status-expired', label: 'Fuera de Tiempo', icon: '🕒' };
        }

        const configs: Record<AppointmentStatus, { class: string; label: string; icon: string }> = {
            PENDING: { class: 'status-warning', label: 'Pendiente', icon: '⏳' },
            CONFIRMED: { class: 'status-success', label: 'Confirmado', icon: '✅' },
            IN_PROGRESS: { class: 'status-primary', label: 'En Progreso', icon: '✂️' },
            COMPLETED: { class: 'status-info', label: 'Completado', icon: '✨' },
            CANCELLED: { class: 'status-error', label: 'Cancelado', icon: '✕' },
            NO_SHOW: { class: 'status-secondary', label: 'No Asistió', icon: '👻' }
        };
        return configs[appointment.status] || { class: 'status-secondary', label: appointment.status, icon: '?' };
    };

    const getFilterCounts = () => {
        const today = getLocalDateString();
        return {
            all: appointments.length,
            upcoming: appointments.filter(a =>
                a.appointmentDate >= today &&
                (a.status === 'CONFIRMED' || a.status === 'PENDING')
            ).length,
            completed: appointments.filter(a => a.status === 'COMPLETED').length,
            cancelled: appointments.filter(a =>
                a.status === 'CANCELLED' || a.status === 'NO_SHOW'
            ).length
        };
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr + 'T00:00:00');
        return {
            day: date.toLocaleDateString('es-AR', { day: '2-digit' }),
            month: date.toLocaleDateString('es-AR', { month: 'short' }).toUpperCase(),
            full: date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
        };
    };

    const counts = getFilterCounts();

    if (loading) {
        return (
            <div className="history-loading">
                <div className="spinner"></div>
                <p>Cargando historial...</p>
            </div>
        );
    }

    return (
        <div className="appointment-history fade-in">
            {/* Header */}
            <header className="history-header">
                <div className="container header-content">
                    <div className="header-left">
                        <button className="back-button" onClick={() => navigate('/client/dashboard')}>
                            ← Volver
                        </button>
                        <h1 className="page-title">📅 Historial de Turnos</h1>
                    </div>
                    <button className="btn btn-primary" onClick={() => navigate('/client/book-appointment')}>
                        + Nuevo Turno
                    </button>
                </div>
            </header>

            <main className="history-main container">
                {/* Stats Summary - New Pill Design */}
                <section className="stats-summary">
                    <div className="stat-item">
                        <span className="stat-number">{counts.all}</span>
                        <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-item stat-success">
                        <span className="stat-number">{counts.completed}</span>
                        <span className="stat-label">Completados</span>
                    </div>
                    <div className="stat-item stat-warning">
                        <span className="stat-number">{counts.upcoming}</span>
                        <span className="stat-label">Próximos</span>
                    </div>
                </section>

                {/* Filters */}
                <section className="filters-section">
                    <div className="filter-tabs">
                        <button
                            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            Todos ({counts.all})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
                            onClick={() => setFilter('upcoming')}
                        >
                            Próximos ({counts.upcoming})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
                            onClick={() => setFilter('completed')}
                        >
                            Completados ({counts.completed})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'cancelled' ? 'active' : ''}`}
                            onClick={() => setFilter('cancelled')}
                        >
                            Cancelados ({counts.cancelled})
                        </button>
                    </div>
                </section>

                {/* Appointments List */}
                <section className="appointments-list">
                    {filteredAppointments.length === 0 ? (
                        <div className="empty-state fade-in">
                            <div className="empty-icon">📅</div>
                            <h4>No hay turnos {filter !== 'all' ? 'en esta categoría' : ''}</h4>
                            <p>
                                {filter === 'upcoming'
                                    ? '¡Agenda tu próximo turno hoy mismo!'
                                    : filter === 'all'
                                        ? 'Aún no tienes turnos registrados.'
                                        : 'No se encontraron turnos para este filtro.'}
                            </p>
                            {filter === 'upcoming' && (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => navigate('/client/book-appointment')}
                                >
                                    Agendar Turno
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="appointments-grid">
                            {filteredAppointments.map((appointment) => {
                                const statusConfig = getStatusConfig(appointment);
                                const dateInfo = formatDate(appointment.appointmentDate);
                                return (
                                    <div key={appointment.id} className="appointment-card fade-in">
                                        <div className="card-date-badge">
                                            <span className="date-day">{dateInfo.day}</span>
                                            <span className="date-month">{dateInfo.month}</span>
                                        </div>

                                        <div className="card-content">
                                            <div className="card-header">
                                                <div className="barber-info">
                                                    <h4 className="barber-name">{appointment.barberName}</h4>
                                                    <p className="branch-name">📍 {appointment.branchName}</p>
                                                </div>
                                                <span className={`status-badge ${statusConfig.class}`}>
                                                    {statusConfig.icon} {statusConfig.label}
                                                </span>
                                            </div>

                                            <div className="card-details">
                                                <div className="detail-item">
                                                    <span className="detail-icon">🕐</span>
                                                    <span>{appointment.startTime} hs</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-icon">✂️</span>
                                                    <span>{appointment.serviceDescription || 'Servicio Barbería'}</span>
                                                </div>
                                                {appointment.notes && (
                                                    <div className="detail-item notes">
                                                        <span><strong>Nota:</strong> {appointment.notes}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {canManage(appointment) && (
                                                <div className="card-actions">
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() => handleManageAppointment(appointment)}
                                                    >
                                                        ⚙️ Gestionar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>

            {/* Appointment Manage Modal */}
            <AppointmentManageModal
                appointment={selectedAppointment}
                isOpen={showManageModal}
                onClose={() => {
                    setShowManageModal(false);
                    setSelectedAppointment(null);
                }}
                onAppointmentUpdated={() => {
                    loadAppointments();
                }}
            />
        </div>
    );
};

export default AppointmentHistory;


